import React, { useEffect, useState } from "react";
import { rtdb } from "../firebase";
import { ref, get } from "firebase/database";
import "./AdminStock.css";

function AdminStock({ onNavigate, onBack }) {
  const [groupedData, setGroupedData] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("All");
  const [rawData, setRawData] = useState([]);

  const getFullTimestamp = (d) => {
    if (!d) return 0;
    const [day, month, year] = d.split("/").map(Number);
    return new Date(year, month - 1, day).getTime();
  };

  useEffect(() => {
    const optimizeFetchStock = async () => {
      try {
        // ⚡ SINGLE FETCH: Get the master Progress node with all data included
        const progressSnap = await get(ref(rtdb, "Progress"));
        if (!progressSnap.exists()) {
          setRawData([]);
          setLoading(false);
          return;
        }

        const progressItems = [];
        progressSnap.forEach((child) => {
          const val = child.val();
          const fgNum = String(val.fgNumber || child.key).trim();
          
          // Filter: Active stock means it is received (s1) but not completely delivered out (s5)
          if (val.s1 === true && val.s5 !== true) {
            
            // Build the tracking timeline array matching your boolean state flags
            const steps = [];
            if (val.s1) steps.push("Receive");
            if (val.s2) steps.push("Treat");
            if (val.s3) steps.push("Paint");
            if (val.s4) steps.push("Pack");

            // Pinpoint current active workstation station location
            let latestStep = "Receive";
            if (val.s4) latestStep = "Pack";
            // Check flags sequentially backward to get the actual current state
            else if (val.s3) latestStep = "Paint";
            else if (val.s2) latestStep = "Treat";

            progressItems.push({
              fgNumber: fgNum,
              steps: steps,
              latestStep: latestStep,
              timestamp: getFullTimestamp(val.receivingDate || val.date),
              part: val.labelType || "Unknown", // ⚡ Pulled directly from Progress node snapshot payload
            });
          }
        });

        setRawData(progressItems);
      } catch (err) {
        console.error("Optimized single-node stock fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    optimizeFetchStock();
  }, []);

  // Combined Filter, Search, Grouping, and Accordion Collapse Initialization Pipeline
  useEffect(() => {
    let enriched = [...rawData];

    if (search.trim()) {
      const q = search.toLowerCase();
      enriched = enriched.filter(
        (f) =>
          f.fgNumber.toLowerCase().includes(q) || f.part.toLowerCase().includes(q)
      );
    }
    
    if (stageFilter !== "All") {
      enriched = enriched.filter((f) => f.latestStep === stageFilter);
    }

    // Sort descending by calculated timestamp
    enriched.sort((a, b) => b.timestamp - a.timestamp);

    const grouped = {};
    enriched.forEach((fg) => {
      const category = fg.part || "Unknown";
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(fg);
    });
    
    setGroupedData(grouped);

    // ⚡ Automatically initialize collapse states for new groups cleanly inside this single effect pipeline
    setCollapsed((prevCollapsed) => {
      const updatedCollapse = { ...prevCollapsed };
      Object.keys(grouped).forEach((cat) => {
        // If the category doesn't have an established collapse boolean yet, initialize it as true
        if (updatedCollapse[cat] === undefined) {
          updatedCollapse[cat] = true;
        }
      });
      return updatedCollapse;
    });

  }, [rawData, search, stageFilter]);

  const toggleCollapse = (cat) =>
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <div className="stock-root">
      <div className="stock-header-section">
        <h1 className="stock-title">Stock Overview</h1>

        {/* Stats Summary Panel */}
        <div className="stock-stats-grid">
          <div className="stat-card">
            <span className="stat-value-stock">
              {rawData.filter((f) => f.latestStep === "Receive").length}
            </span>
            <span className="stat-label-stock">Total WIP</span>
          </div>

          <div className="stat-card">
            <span className="stat-value-stock">
              {rawData.filter((f) => f.latestStep === "Pack").length}
            </span>
            <span className="stat-label-stock">Ready to Deliver</span>
          </div>

          <div className="stat-card highlight">
            <span className="stat-value-stock">
              {Object.keys(groupedData).length}
            </span>
            <span className="stat-label-stock">Active Parts</span>
          </div>
        </div>
      </div>

      <div className="stock-controls">
        <div className="stock-search-wrapper">
          <input
            className="stock-search-input"
            placeholder="Search FG or Part Number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {(search || stageFilter !== "All") && (
            <button
              className="stock-reset-link"
              onClick={() => {
                setSearch("");
                setStageFilter("All");
              }}
            >
              Reset Filters
            </button>
          )}
        </div>

        <div className="stock-filter-pills">
          {["All", "Receive", "Treat", "Paint", "Pack"].map((s) => (
            <button
              key={s}
              className={`filter-pill ${stageFilter === s ? "active" : ""}`}
              onClick={() => setStageFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div
          className="loading-shimmer"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            minHeight: "100px",
          }}
        >
          Analyzing Production Lines...
        </div>
      ) : (
        Object.keys(groupedData)
          .sort()
          .map((category) => (
            <div key={category} className="stock-category-group">
              <div
                className="category-header"
                onClick={() => toggleCollapse(category)}
              >
                <div className="category-info">
                  <span
                    className={`arrow ${collapsed[category] ? "" : "rotated"}`}
                  >
                    ▶
                  </span>
                  <h3>{category}</h3>
                </div>
                <span className="count-badge">
                  {groupedData[category].length} Units
                </span>
              </div>

              <div
                className={`stock-list ${
                  collapsed[category] ? "collapsed" : ""
                }`}
              >
                {groupedData[category].map((fg) => (
                  <div
                    key={fg.fgNumber}
                    className="stock-card"
                    onClick={() => onNavigate?.(fg.fgNumber, fg.latestStep)}
                  >
                    <div className="card-left">
                      <span className="fg-id">{fg.fgNumber}</span>
                      <div className="mini-progress-track">
                        {["Receive", "Treat", "Paint", "Pack"].map((step) => (
                          <div
                            key={step}
                            className={`progress-dot ${
                              fg.steps.includes(step) ? "filled" : ""
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="card-right">
                      <span className={`status-tag tag-${fg.latestStep}`}>
                        {fg.latestStep}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
      )}
    </div>
  );
}

export default AdminStock;